<template>
  <div
    class="admin-page"
    :data-panel-release="$panelRelease"
  >
    <admin-page-header title="İletişim Talepleri" />
    <v-card class="admin-table-card">
      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          outlined
          dense
        >
          {{ error }}
        </v-alert>
        <v-row align="center">
          <v-col
            cols="12"
            md="7"
          >
            <v-text-field
              v-model="query"
              label="Ad, e-posta, telefon veya mesaj ara"
              maxlength="200"
              clearable
              hide-details
              @keyup.enter="applyFilters"
            />
          </v-col>
          <v-col
            cols="12"
            md="3"
          >
            <v-select
              v-model="status"
              :items="statuses"
              label="Durum"
              clearable
              hide-details
              @change="applyFilters"
            />
          </v-col>
          <v-col
            cols="12"
            md="2"
          >
            <v-btn
              color="primary"
              :loading="loading"
              @click="applyFilters"
            >
              Ara / Yenile
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="{ 'items-per-page-options': [25, 50, 100], 'items-per-page-text': 'Sayfa başına' }"
        no-data-text="İletişim talebi bulunamadı."
        loading-text="Talepler yükleniyor..."
        disable-sort
      >
        <template #[`item.created`]="{ item }">
          {{ formatDate(item.created) }}
          <div
            v-if="item.status !== 'RESOLVED'"
            class="text-caption"
          >
            {{ waitingTime(item.created) }}
          </div>
        </template>
        <template #[`item.review`]="{ item }">
          <template v-if="reviewError">
            Yüklenemedi
          </template>
          <template v-else>
            {{ reviews[item.id] ? reviews[item.id].assignee || 'Atanmadı' : 'Atanmadı' }}<div class="text-caption">
              {{ priorityLabel(reviews[item.id] && reviews[item.id].priority) }}
            </div>
          </template>
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip
            small
            :color="statusColor(item.status)"
          >
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
        <template #[`item.actions`]="{ item }">
          <v-btn
            small
            text
            color="primary"
            @click="openDetail(item)"
          >
            Detay
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
    <v-dialog
      v-model="dialog"
      max-width="1080"
      aria-labelledby="contact-detail-title"
      :persistent="saving || reviewBusy || reviewDirty"
    >
      <v-card
        v-if="selected"
        class="admin-dialog-card contact-dialog-card"
      >
        <v-card-title id="contact-detail-title">
          Talep #{{ selected.id }}
        </v-card-title>
        <v-card-text class="contact-review-layout">
          <div>
            <v-alert
              v-if="detailError"
              type="error"
              outlined
              dense
            >
              {{ detailError }}
            </v-alert>
            <dl class="contact-details">
              <dt>Ad Soyad</dt><dd>{{ selected.name }}</dd>
              <dt>E-posta</dt><dd>{{ selected.email }}</dd>
              <dt>Telefon</dt><dd>{{ selected.phone || '—' }}</dd>
              <dt>Tarih</dt><dd>{{ formatDate(selected.created) }}</dd>
              <dt>Aydınlatma metni</dt><dd>{{ selected.privacyAcknowledged ? 'Okundu onayı alındı' : 'Onay kaydı yok' }}</dd>
              <dt>Mesaj</dt><dd class="contact-message">
                {{ selected.message }}
              </dd>
            </dl>
            <v-select
              v-model="selectedStatus"
              :items="statuses"
              label="Talep durumu"
              :disabled="saving"
              class="mt-6"
            />
            <p
              v-if="selected.statusUpdatedBy"
              class="text-caption"
            >
              Son güncelleyen: {{ selected.statusUpdatedBy }} · {{ formatDate(selected.modified) }}
            </p>
          </div>
          <support-review-editor
            :key="selected.id"
            channel="CONTACT"
            :record-id="selected.id"
            @busy="reviewBusy = $event"
            @dirty="reviewDirty = $event"
            @saved="reviewSaved"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="saving || reviewBusy || reviewDirty"
            @click="dialog = false"
          >
            Kapat
          </v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="selectedStatus === selected.status"
            @click="saveStatus"
          >
            Durumu Kaydet
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import axios from 'axios'
import { getAccessToken } from '@/utils/panelSession'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import SupportReviewEditor from '@/components/admin/SupportReviewEditor.vue'
import { formatAdminDate, parseAdminDate } from '@/utils/adminDate'
import { resolveApiErrorMessage } from '@/utils/apiErrors'

export default {
  components: { AdminPageHeader, SupportReviewEditor },
  data: () => ({
    items: [],
    total: 0,
    loading: false,
    saving: false,
    error: '',
    detailError: '',
    query: '',
    status: null,
    appliedQuery: '',
    appliedStatus: null,
    options: { page: 1, itemsPerPage: 25 },
    requestSequence: 0,
    dialog: false,
    selected: null,
    selectedStatus: null,
    reviews: {},
    reviewError: '',
    reviewBusy: false,
    reviewDirty: false,
    detailSequence: 0,
    statuses: [{ text: 'Yeni', value: 'NEW' }, { text: 'İşlemde', value: 'IN_PROGRESS' }, { text: 'Tamamlandı', value: 'RESOLVED' }],
    headers: [
      { text: 'Tarih', value: 'created', sortable: false },
      { text: 'Ad Soyad', value: 'name', sortable: false },
      { text: 'E-posta', value: 'email', sortable: false },
      { text: 'Durum', value: 'status', sortable: false },
      { text: 'Sorumlu / Öncelik', value: 'review', sortable: false },
      { text: '', value: 'actions', sortable: false },
    ],
  }),
  computed: {
    apiBase() { return `${process.env.VUE_APP_BASE_API_URL}admin/contact-requests` },
    requestOptions() { return { headers: { Authorization: `Bearer ${getAccessToken()}` } } },
  },
  watch: { options: { deep: true, handler() { this.load() } } },
  beforeRouteLeave(to, from, next) { next(!this.saving && !this.reviewBusy && !this.reviewDirty) },
  beforeDestroy() { this.requestSequence += 1; this.detailSequence += 1 },
  methods: {
    reviewSaved(row) { this.$set(this.reviews, row.recordId, row) },
    priorityLabel(value) {
      return {
        LOW: 'Düşük', NORMAL: 'Normal', HIGH: 'Yüksek', URGENT: 'Acil',
      }[value || 'NORMAL']
    },
    waitingTime(value) {
      const date = parseAdminDate(value)

      return date ? `${Math.max(0, Math.floor((Date.now() - date.getTime()) / 86400000))} gündür açık` : '-'
    },
    statusLabel(value) { return this.statuses.find(s => s.value === value)?.text || value },
    statusColor(value) { return { NEW: 'primary', IN_PROGRESS: 'warning', RESOLVED: 'success' }[value] },
    formatDate: formatAdminDate,
    applyFilters() {
      this.appliedQuery = (this.query || '').trim()
      this.appliedStatus = this.status
      if (this.options.page !== 1) this.options.page = 1
      else this.load()
    },
    async load() {
      this.requestSequence += 1
      const sequence = this.requestSequence
      this.loading = true
      this.error = ''
      try {
        const { data } = await axios.get(this.apiBase, {
          ...this.requestOptions,
          params: {
            page: this.options.page - 1, size: this.options.itemsPerPage, query: this.appliedQuery || undefined, status: this.appliedStatus || undefined,
          },
        })
        if (sequence !== this.requestSequence) return
        this.items = data.content
        this.total = data.totalElements
        this.reviews = {}; this.reviewError = ''
        if (this.items.length) {
          try {
            const reviewResponse = await axios.get(`${process.env.VUE_APP_BASE_API_URL}admin/support-reviews/CONTACT`, { ...this.requestOptions, params: { ids: this.items.map(row => row.id).join(',') } })
            if (sequence === this.requestSequence) this.reviews = Object.fromEntries(reviewResponse.data.map(row => [row.recordId, row]))
          } catch (reviewError) { if (sequence === this.requestSequence) this.reviewError = 'İnceleme bilgileri yüklenemedi.' }
        }
      } catch (error) {
        if (sequence === this.requestSequence) { this.items = []; this.total = 0; this.error = 'Talepler yüklenemedi. Lütfen tekrar deneyin.' }
      } finally { if (sequence === this.requestSequence) this.loading = false }
    },
    async openDetail(item) {
      if (this.saving || this.reviewBusy || this.reviewDirty) return
      this.detailSequence += 1
      const sequence = this.detailSequence
      this.error = ''
      try {
        const { data } = await axios.get(`${this.apiBase}/${item.id}`, this.requestOptions)
        if (sequence !== this.detailSequence) return
        this.selected = data
        this.selectedStatus = data.status
        this.detailError = ''
        this.dialog = true
      } catch (error) { this.error = 'Talep detayı yüklenemedi. Lütfen tekrar deneyin.' }
    },
    async saveStatus() {
      if (this.saving || !this.selected || this.selectedStatus === this.selected.status) return
      this.saving = true
      this.detailError = ''
      try {
        const { data } = await axios.patch(`${this.apiBase}/${this.selected.id}/status`, { status: this.selectedStatus }, this.requestOptions)
        this.selected = data
        await this.load()
      } catch (error) { this.detailError = resolveApiErrorMessage(error, 'Durum güncellenemedi. Lütfen tekrar deneyin.') } finally { this.saving = false }
    },
  },
}
</script>
<style scoped>
.contact-dialog-card { display: flex; flex-direction: column; max-height: 90vh; }
.contact-dialog-card > .v-card__title, .contact-dialog-card > .v-card__actions { flex: 0 0 auto; }
.contact-dialog-card > .v-card__actions { border-top: 1px solid var(--admin-border); flex-wrap: wrap; gap: 8px; }
.contact-dialog-card > .v-card__text { overflow: auto; min-height: 0; }
.theme--dark .contact-dialog-card a { color: #ff94a5; }
.contact-review-layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 24px; }
@media (max-width: 800px) { .contact-review-layout { grid-template-columns: minmax(0, 1fr); } }
.contact-details { display: grid; grid-template-columns: 140px minmax(0, 1fr); gap: 12px; }
.contact-details dt { font-weight: 600; }
.contact-details dd { margin: 0; overflow-wrap: anywhere; }
.contact-message { white-space: pre-wrap; }
@media (max-width: 600px) { .contact-details { grid-template-columns: minmax(0, 1fr); gap: 8px; } .contact-details dd { margin-bottom: 8px; } }
</style>
